import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useManualQuery } from "graphql-hooks";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Button,
  Avatar,
  TextField,
  Tooltip,
  TablePagination,
  TableSortLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Collapse,
  Link,
} from "@mui/material";
import AlertPopup from "./components/AlertPopup";
import FloatingMenu from "./components/FloatingMenu";
import EmptyState from "./components/EmptyState";
import {
  AssignmentTurnedInOutlined,
  FactCheckOutlined,
  VisibilityOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowUpward,
  FilterListOutlined,
  ArrowDownward,
  ArrowDropUp,
  ArrowDropDown,
  HistoryToggleOffOutlined,
  ModeCommentOutlined,
  ReceiptLongOutlined,
} from "@mui/icons-material";

// Import toy images
import photo1 from "./assets/ToysList/photo1.png";
import photo2 from "./assets/ToysList/photo2.png";
import photo3 from "./assets/ToysList/photo3.png";
import photo4 from "./assets/ToysList/photo4.png";
import photo5 from "./assets/ToysList/photo5.png";
import photo6 from "./assets/ToysList/photo6.png";
import useIsTablet from "./hooks/useIsTablet";
import useIsMobile from "./hooks/useIsMobile";

import { MY_PURCHASED_TOY_LIST } from "./graphQL/toy_qry";

// Status Options
const STATUS_OPTIONS = [
  "All", "Purchased", "Shipped",
  "Received", "Accepted",
];

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

const PurchasedToysList = () => {
  const isTablet = useIsTablet(1360); // Use 1360px breakpoint for tablet size
  const isMobile = useIsTablet(833); // Use 833px breakpoint for mobile size
  const [statusFilter, setStatusFilter] = useState("All");
  const [mobileSortBy, setMobileSortBy] = useState("purchaseDate");
  const [sortPanelOpen, setSortPanelOpen] = useState(false);
  const [orderBy, setOrderBy] = useState("purchaseDate");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [toys, setToys] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  
  const formatter = new Intl.DateTimeFormat("en-us", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });
  
  const dayFormat = day => {
    const dayDate = new Date(day);
    const dayFormat = formatter.formatToParts(dayDate);
    const newFormat =
      dayFormat.find(a => a.type == "month").value +
      " " +
      dayFormat.find(a => a.type == "day").value +
      ", " +
      dayFormat.find(a => a.type == "year").value;
    return newFormat;
  };
  const [getPurchasedToyList] = useManualQuery(MY_PURCHASED_TOY_LIST);
  const FetchPurchasedToyList = async () => {
    const {
      loading: loadingToyList,
      error: errorToyList,
      data: orderedToyList,
    } = await getPurchasedToyList();
    if (!loadingToyList && orderedToyList) {  
      console.log(orderedToyList);
      setToys(
          orderedToyList.myPurchasedToyList.map(order => ({
            id: order.toyId.toyId,
            uuid: order.toyId.uuid,
            photo: order.toyId.toyImages[0]?.fileName || "",
            title: order.toyId.title,
            price: order.toyId.sellingPrice,
            originalPrice: order.toyId.originalPrice,
            purchaseDate: dayFormat(order.orderDate),
            status: order.toyId.status === "Sold" ? "Purchased" : order.toyId.status,
          }))
        );
      setIsLoaded(true);
    } else {
      console.log("error: ", errorToyList);
      setIsLoaded(true);
    }
  };
  
  useEffect(() => {
    (async () => FetchPurchasedToyList())();
  }, []);

  useEffect(() => {
    if (isMobile && rowsPerPage === 10) setRowsPerPage(5);
    else if (!isMobile && rowsPerPage === 5) setRowsPerPage(10);
  }, [rowsPerPage, isMobile, isTablet]);

  // Handle click outside to close sort panel in Mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the sort panel container
      const sortPanel = document.querySelector("[data-sort-panel]");
      if (sortPanel && !sortPanel.contains(event.target) && sortPanelOpen) {
        setSortPanelOpen(false);
      }
    };

    if (sortPanelOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sortPanelOpen]);

  // Set floating menu place based on screen size
  useEffect(() => {
    const handleResize = () => {      
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);
  }, []);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  // Filter change handlers
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0); // Reset to first page when filter changes
  };

  const handleMobileSortChange = (event) => {
    const sortValue = event.target.value;
    setMobileSortBy(sortValue);
    handleRequestSort(sortValue);
    setPage(0); // Reset to first page when sort changes
  };

  const handleCancelAction = () => {
    // setAlertPopup({ open: false, type: "", toyId: null });
  };

  // Sorting handler
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setMobileSortBy(property)
  };

  // Filter and sort data
  const getFilteredData = () => {
    let filteredData = toys;

    // Filter by Status
    if (statusFilter !== "All") {
      filteredData = filteredData.filter((toy) => toy.status === statusFilter);
    }

    // Sort data
    if (orderBy) {
      filteredData.sort((a, b) => {
        let aValue = a[orderBy];
        let bValue = b[orderBy];

        // Handle price sorting (remove $ and convert to number)
        if (orderBy === "price") {
          aValue = parseFloat(aValue.replace("$", ""));
          bValue = parseFloat(bValue.replace("$", ""));
        }

        // Handle title sorting (case insensitive)
        if (orderBy === "title") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        // Handle date sorting for purchaseDate
        if (orderBy === "purchaseDate") {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }

        // Handle status sorting (case insensitive)
        if (orderBy === "status") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return order === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return order === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredData;
  };

  const filteredData = getFilteredData();

  // Get current page data from filtered results
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  //console.log("paginatedData___", paginatedData);
  return (
    <>
      <Helmet>
        <title>R3PLAY - Purchased Toys</title>
        <meta
          name="description"
          content="View your purchased toys and manage your order details"
        />
        <meta
          property="keywords"
          content="Purchased toys, toy purchases, toy collection"
        />
      </Helmet>
      {/* Mobile View - Only shows on screens < 834px */}
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          minHeight: "100vh",
          py: 4,
          px: "16px",
          display: "none",
          "@media (max-width: 833px)": {
            display: "block",
          },
        }}
      >
        {/* Mobile Header */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            p: 2,
            mb: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#333",
              mb: 1,
            }}
          >
            My Purchased Toys
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#666",
              mb: 2,
            }}
          >
            Enjoy the journey you've taken so far in purchasing toys.
          </Typography>

          {/* Mobile Filter Controls */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: "row",
            }}
          >
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              sx={{
                width: "50%",
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  backgroundColor: "white",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                    borderWidth: "1px",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#666",
                  fontSize: "14px",
                  "&.Mui-focused": {
                    color: "#666",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
              }}
              SelectProps={{
                IconComponent: ArrowDropDown,
              }}
            >
              {STATUS_OPTIONS.map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        {/* Mobile Sort Panel */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 2,
            width: "100%",
          }}
        >
          <Box sx={{ width: "230px", position: "relative" }} data-sort-panel>
            <TextField
              label="Sort by"
              value={
                mobileSortBy
                  ? (() => {
                      const options = {
                        title: "Title",
                        price: "Price",
                        purchaseDate: "Purchase Date",
                        status: "Status",
                      };
                      return options[mobileSortBy] || "";
                    })()
                  : ""
              }
              onClick={() => setSortPanelOpen(!sortPanelOpen)}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {mobileSortBy &&
                      (order === "desc" ? (
                        <ArrowDownward sx={{ fontSize: 20, color: "#666" }} />
                      ) : (
                        <ArrowUpward sx={{ fontSize: 20, color: "#666" }} />
                      ))}
                    {sortPanelOpen ? (
                      <ArrowDropUp sx={{ color: "#666" }} />
                    ) : (
                      <ArrowDropDown sx={{ color: "#666" }} />
                    )}
                  </Box>
                ),
              }}
              sx={{
                width: "230px",
                cursor: "pointer",
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  backgroundColor: "white",
                  cursor: "pointer",
                  paddingRight: "8px",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#d0d0d0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                    borderWidth: "1px",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#666",
                  fontSize: "14px",
                  "&.Mui-focused": {
                    color: "#666",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                    pr: 0,
                },
                "& .MuiOutlinedInput-input": {
                  cursor: "pointer",
                },
              }}
            />

            {/* Sort options panel */}
            {sortPanelOpen && (
              <Box
                sx={{
                  position: "absolute",
                  top: "40px",
                  left: 0,
                  right: 0,
                  backgroundColor: "white",
                  border: "1px solid #e0e0e0",
                  borderTop: "none",
                  borderRadius: "0 0 4px 4px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  zIndex: 1000,
                }}
              >
                <RadioGroup
                  value={mobileSortBy}
                  onChange={(e) => {
                    setMobileSortBy(e.target.value);
                    setOrder("desc");
                    setOrderBy(e.target.value);
                    setPage(0);
                    //setSortPanelOpen(false);
                  }}
                >
                  {[
                    { value: "title", label: "Title" },
                    { value: "price", label: "Price" },
                    { value: "purchaseDate", label: "Purchase Date" },
                    { value: "status", label: "Status" },
                  ].map((option) => (
                    <Box
                      key={option.value}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 1,
                        pl: 0,
                        py: 0.5,
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    >
                      <FormControlLabel
                        value={option.value}
                        control={
                          <Radio
                            size="small"
                            sx={{
                              color: "#666",
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ fontSize: "14px", color: "#333" }}>
                            {option.label}
                          </Typography>
                        }
                        sx={{ margin: 0, flex: 1 }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          width: "55px",
                          justifyContent: "space-between",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMobileSortBy(option.value);
                            setOrder("desc");
                            setOrderBy(option.value);
                            setPage(0);
                            setSortPanelOpen(false);
                          }}
                          sx={{
                            p: 0.25,
                            backgroundColor:
                              mobileSortBy === option.value && order === "desc"
                                ? "#fce4ec"
                                : "transparent",
                          }}
                        >
                          <ArrowDownward
                            fontSize="small"
                            sx={{ fontSize: 20, color: "#666" }}
                          />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMobileSortBy(option.value);
                            setOrder("asc");
                            setOrderBy(option.value);
                            setPage(0);
                            setSortPanelOpen(false);
                          }}
                          sx={{
                            p: 0.25,
                            backgroundColor:
                              mobileSortBy === option.value && order === "asc"
                                ? "#fce4ec"
                                : "transparent",
                          }}
                        >
                          <ArrowUpward
                            fontSize="small"
                            sx={{ fontSize: 20, color: "#666" }}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </RadioGroup>
              </Box>
            )}
          </Box>
        </Box>

        {/* Mobile Cards */}
        {!isLoaded ? (
          <Box p={4} textAlign="center">Loading...</Box>
        ) : isLoaded && toys.length === 0 ? (
          <EmptyState 
            type="listings"
            title="No Purchased Toys Available"
            description="You haven't purchased any toys yet."
          />
        ) : (
          <>
            {paginatedData.map((toy, index) => (
          <Box
            key={toy.id}
            sx={{
              backgroundColor: index % 2 === 0 ? "#FDECF0" : "white", //#FEF3F5
              borderRadius: "8px",
              mb: 2,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              overflow: "hidden",
            }}
          >
            <Table>
              <TableBody>
                {/* Row 1: Image + Title */}
                <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                  <TableCell sx={{ py: 2, width: "125px", border: "none" }}>
                    <Avatar
                      src={toy.photo}
                      alt={toy.title}
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "8px",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 2, border: "none" }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#333",
                        fontWeight: "500",
                        lineHeight: 1.4,
                      }}
                    >
                      {toy.title}
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* Row 2: Price + Purchase Date */}
                <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                  <TableCell
                    sx={{
                      py: 2,
                      border: "none",
                      verticalAlign: "top"
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#666",
                        fontWeight: "bold",
                        mb: 0.5,
                      }}
                    >
                      Price
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        {toy.price}
                      </Typography>
                      {toy.originalPrice && (
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#999",
                            fontWeight: "400",
                            textDecoration: "line-through",
                          }}
                        >
                          {toy.originalPrice}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 2,
                      border: "none",
                      verticalAlign: "top"
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#666",
                        fontWeight: "bold",
                        mb: 0.5,
                      }}
                    >
                      Purchase Date
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#333",
                      }}
                    >
                      {toy.purchaseDate}
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* Row 3: Status */}
                <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                  <TableCell
                    sx={{
                      py: 2,
                      border: "none",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#666",
                        fontWeight: "bold",
                        mb: 0.5,
                      }}
                    >
                      Status
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: "white",
                        color: "#333",
                        border: "1px solid #000",
                        fontSize: "12px",
                        fontWeight: "500",
                        width: "100px",
                        height: "30px",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      {toy.status}
                    </Box>
                  </TableCell>
                </TableRow>

                {/* Row 4: Actions */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ pt: 2, pb: 0, border: "none" }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-start",
                      }}
                    >
                      <StyledTooltip title="View product details">
                        <IconButton
                          size="small"
                          sx={{
                            color: "#666",
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.04)",
                            },
                          }}
                        >
                          <Link component={RouterLink} to={`/toy/${toy.uuid}`} target="_blank" color="black" underline="none"><VisibilityOutlined fontSize="small" /></Link>
                        </IconButton>
                      </StyledTooltip>
                      <StyledTooltip title="View delivery timeline">
                        <IconButton
                          size="small"
                          sx={{
                            color: "#666",
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.04)",
                            },
                          }}
                        >
                          <HistoryToggleOffOutlined fontSize="small" />
                        </IconButton>
                      </StyledTooltip>
                      <StyledTooltip title="View comments">
                        <IconButton
                          size="small"
                          sx={{
                            color: "#666",
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.04)",
                            },
                          }}
                        >
                          <ModeCommentOutlined fontSize="small" />
                        </IconButton>
                      </StyledTooltip>
                      <StyledTooltip title="View order details">
                        <IconButton
                          size="small"
                          sx={{
                            color: "#666",
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.04)",
                            },
                          }}
                        >
                          <ReceiptLongOutlined fontSize="small" />
                        </IconButton>
                      </StyledTooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* Card bottom padding */}
            <Box sx={{ pb: 2 }} />
          </Box>
        ))}

        {/* Mobile Pagination */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <TablePagination
            rowsPerPageOptions={[5, 15, 20]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              "& .MuiTablePagination-toolbar": {
                paddingLeft: 1,
                paddingRight: 1,
                minHeight: "52px",
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "14px",
                color: "#666",
              },
              "& .MuiTablePagination-select": {
                fontSize: "14px",
              },
              "& .MuiTablePagination-actions": {
                "& .MuiIconButton-root": {
                  padding: "6px",
                },
              },
            }}
          />
        </Box>
        </>
        )}
      </Box>

      {/* Desktop/Tablet View - Only shows on screens >= 834px */}
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          minHeight: "100vh",
          py: 4,
          px: "100px",
          "@media (max-width: 1360px)": {
            px: "16px",
          },
          "@media (max-width: 833px)": {
            display: "none",
          },
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            height: "91px",
            p: 1,
            pl: 2,
            pr: 2,
            mb: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              "@media (max-width: 833px)": {
                flexDirection: "column",
                gap: 2,
              },
            }}
          >
            {/* Title Section */}
            <Box>
              <Typography
                sx={{
                  fontSize: "30px",
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                My Purchased Toys
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#666",
                }}
              >
                Enjoy the journey you've taken so far in purchasing toys.
              </Typography>
            </Box>

            {/* Filter Controls */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                "@media (max-width: 833px)": {
                  width: "100%",
                  justifyContent: "flex-start",
                },
              }}
            >
              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                sx={{
                  width: "150px",
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    backgroundColor: "white",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                      borderWidth: "1px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    fontSize: "14px",
                    "&.Mui-focused": {
                      color: "#666",
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                }}
                SelectProps={{
                  IconComponent: ArrowDropDown,
                }}
              >
                {STATUS_OPTIONS.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </Box>

        {/* Table */}
        {!isLoaded ? (
          <Box p={4} textAlign="center">Loading...</Box>
        ) : isLoaded && toys.length === 0 ? (
          <EmptyState 
            type="listings"
            title="No Purchased Toys Available"
            description="You haven't purchased any toys yet."
          />
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "white" }}>
                <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                  Photo
                </TableCell>
                <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                  <TableSortLabel
                    active={orderBy === "title"}
                    direction={orderBy === "title" ? order : "asc"}
                    onClick={() => handleRequestSort("title")}
                    sx={{
                      fontWeight: "600",
                      color: "#333",
                      "&.Mui-active": {
                        color: "#333",
                      },
                      "&:hover": {
                        color: "#333",
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "#333 !important",
                      },
                    }}
                  >
                    Title/Summary
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                  <TableSortLabel
                    active={orderBy === "price"}
                    direction={orderBy === "price" ? order : "asc"}
                    onClick={() => handleRequestSort("price")}
                    sx={{
                      fontWeight: "600",
                      color: "#333",
                      "&.Mui-active": {
                        color: "#333",
                      },
                      "&:hover": {
                        color: "#333",
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "#333 !important",
                      },
                    }}
                  >
                    Price
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                  <TableSortLabel
                    active={orderBy === "purchaseDate"}
                    direction={orderBy === "purchaseDate" ? order : "asc"}
                    onClick={() => handleRequestSort("purchaseDate")}
                    sx={{
                      fontWeight: "600",
                      color: "#333",
                      "&.Mui-active": {
                        color: "#333",
                      },
                      "&:hover": {
                        color: "#333",
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "#333 !important",
                      },
                    }}
                  >
                    Purchase Date
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                  <TableSortLabel
                    active={orderBy === "status"}
                    direction={orderBy === "status" ? order : "asc"}
                    onClick={() => handleRequestSort("status")}
                    sx={{
                      fontWeight: "600",
                      color: "#333",
                      "&.Mui-active": {
                        color: "#333",
                      },
                      "&:hover": {
                        color: "#333",
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "#333 !important",
                      },
                    }}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "600",
                    color: "#333",
                    py: 2,
                    minWidth: "150px",
                    textAlign: "center",
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((toy) => (
                <React.Fragment key={toy.id}>
                  <TableRow
                    sx={{
                      "&:hover": {
                        backgroundColor: "#f9f9f9",
                      },
                      ...(isTablet && {
                        "& td": {
                          borderBottom: "none",
                        },
                      }),
                    }}
                  >
                    <TableCell sx={{ py: 2, pb: isTablet ? 0 : 2 }}>
                      <Avatar
                        src={toy.photo}
                        alt={toy.title}
                        sx={{
                          width: isTablet ? 40 : 80,
                          height: isTablet ? 40 : 80,
                          borderRadius: "8px",
                        }}
                      />
                    </TableCell>

                    {/* Title Cell - Spans 6 columns on tablet, normal on desktop */}
                    <TableCell
                      colSpan={isTablet ? 6 : 1}
                      sx={{
                        py: isTablet ? 1 : 2,
                        maxWidth: "300px",
                        "@media (max-width: 1360px)": {
                          maxWidth: "none",
                          paddingBottom: 0,
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#333",
                          fontWeight: "500",
                          lineHeight: 1.4,
                        }}
                      >
                        {toy.title}
                      </Typography>
                    </TableCell>

                    {/* Desktop columns - hidden on tablet */}
                    {!isTablet && (
                      <>
                        <TableCell sx={{ py: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                                fontWeight: "500",
                              }}
                            >
                              {toy.price}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#999",
                                fontWeight: "400",
                                textDecoration: "line-through",
                              }}
                            >
                              {toy.originalPrice}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                            }}
                          >
                            {toy.purchaseDate}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Box
                            sx={{
                              backgroundColor: "white",
                              color: "#333",
                              border: "1px solid #000",
                              fontSize: "12px",
                              fontWeight: "500",
                              width: "100px",
                              height: "30px",
                              minWidth: "80px",
                              borderRadius: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center",
                            }}
                          >
                            {toy.status}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "center",
                            }}
                          >
                            <StyledTooltip title="View product details">
                              <IconButton
                                size="small"
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <Link component={RouterLink} to={`/toy/${toy.uuid}`} target="_blank" color="black" underline="none"><VisibilityOutlined fontSize="small" /></Link>
                              </IconButton>
                            </StyledTooltip>
                            <StyledTooltip title="View delivery timeline">
                              <IconButton
                                size="small"
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <HistoryToggleOffOutlined fontSize="small" />
                              </IconButton>
                            </StyledTooltip>
                            <StyledTooltip title="View comments">
                              <IconButton
                                size="small"
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <ModeCommentOutlined fontSize="small" />
                              </IconButton>
                            </StyledTooltip>
                            <StyledTooltip title="View order details">
                              <IconButton
                                size="small"
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <ReceiptLongOutlined fontSize="small" />
                              </IconButton>
                            </StyledTooltip>
                          </Box>
                        </TableCell>
                      </>
                    )}
                  </TableRow>

                  {/* Second row for tablet view */}
                  {isTablet && (
                    <TableRow
                      key={`${toy.id}-details`}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f9f9f9",
                        },
                        "& td": {
                          borderTop: "none",
                        },
                      }}
                    >
                      {/* Empty cell for Photo and Title column */}

                      <TableCell sx={{ py: 0 }}></TableCell>
                      <TableCell sx={{ py: 0 }}></TableCell>

                      <TableCell sx={{ pt: 0, pb: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                              fontWeight: "500",
                            }}
                          >
                            {toy.price}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#999",
                              fontWeight: "400",
                              textDecoration: "line-through",
                            }}
                          >
                            {toy.originalPrice}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ pt: 0, pb: 1 }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#333",
                          }}
                        >
                          {toy.purchaseDate}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ pt: 0, pb: 2 }}>
                        <Box
                          sx={{
                            backgroundColor: "white",
                            color: "#333",
                            border: "1px solid #000",
                            fontSize: "12px",
                            fontWeight: "500",
                            width: "100px",
                            height: "30px",
                            minWidth: "80px",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                          }}
                        >
                          {toy.status}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ pt: 0, pb: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "center",
                          }}
                        >
                          <StyledTooltip title="View product details">
                            <IconButton
                              size="small"
                              sx={{
                                color: "#666",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <Link component={RouterLink} to={`/toy/${toy.uuid}`} target="_blank" color="black" underline="none"><VisibilityOutlined fontSize="small" /></Link>
                            </IconButton>
                          </StyledTooltip>
                          <StyledTooltip title="View delivery timeline">
                            <IconButton
                              size="small"
                              sx={{
                                color: "#666",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <HistoryToggleOffOutlined fontSize="small" />
                            </IconButton>
                          </StyledTooltip>
                          <StyledTooltip title="View comments">
                            <IconButton
                              size="small"
                              sx={{
                                color: "#666",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <ModeCommentOutlined fontSize="small" />
                            </IconButton>
                          </StyledTooltip>
                          <StyledTooltip title="View order details">
                            <IconButton
                              size="small"
                              sx={{
                                color: "#666",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <ReceiptLongOutlined fontSize="small" />
                            </IconButton>
                          </StyledTooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 15, 20]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid #e0e0e0",
              "& .MuiTablePagination-toolbar": {
                paddingLeft: 2,
                paddingRight: 2,
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "14px",
                color: "#666",
              },
              "& .MuiTablePagination-select": {
                fontSize: "14px",
              },
            }}
          />
        </TableContainer>
        )}
      </Box>
      <FloatingMenu left={floatMenuLeftMargin} />
    </>
  );
};

export default PurchasedToysList;
